import { DEFAULT_DATE_FORMAT } from 'common/constants';
import { isValid, startOfToday } from 'date-fns';

import type { DatePickerProps } from './DatePicker';
import type { DatePickerProps as DsDatePickerProps } from '@natter-co/design-system';

/** The legacy wrapper's own placeholder default, which it applies when a caller gives none. */
const LEGACY_DEFAULT_PLACEHOLDER = 'Select a date';

export type ToDsPropsParams = Pick<
  DatePickerProps,
  'error' | 'format' | 'fullWidth' | 'hint' | 'label' | 'minDate' | 'placeholder' | 'variant'
>;

export type DatePickerDsProps = Pick<DsDatePickerProps, 'error' | 'helperText' | 'label' | 'min'>;

/**
 * The refreshed field's lower bound: today, or the caller's `minDate` when that is later.
 *
 * The legacy wrapper sets `disablePast` on MUI-X unconditionally, so past days have never been
 * selectable through it whatever `minDate` says, and the wizard's date field relies on that - it
 * passes no `minDate`, and its schema only rejects a past start TIME on today's date. Mapping
 * `minDate` alone would let a host pick and save a past date the moment the flag is on.
 */
const earliestSelectable = (minDate: Date | undefined) => {
  const today = startOfToday();

  return minDate !== undefined && minDate > today ? minDate : today;
};

/**
 * Maps the legacy MUI-X `DatePicker` props onto the Design System 2.0 `DatePicker` API, or refuses.
 *
 * TODO(HNS-264): delete this mapper and collapse the wrapper when the refresh flag retires.
 *
 * ── Two differences this mapping accepts rather than refuses ───────────────────────────────────
 *
 * Both are user-visible, both are the refreshed component's own design, and neither can be
 * expressed as a prop - so refusing on them would mean this field never refreshes at all.
 *
 * The DATE FORMAT changes. The legacy field displays `EEEE MMMM do, yyyy` ("Thursday March 5th,
 * 2026"); the refreshed one owns a single short format for both parsing and display and exposes no
 * prop to change it. Adopting it therefore shows `05/03/2026` instead. That is a real ambiguity
 * rather than a preference - the same string reads as 3 May to an American reader, and this is the
 * surface where a host schedules an event - so it is called out here rather than left to be found.
 * Localising the refreshed field is tracked separately; see the date-field ticket under HNS-373.
 *
 * The CLEAR BUTTON goes. The legacy field shows one while it holds a value; the refreshed field
 * has no such affordance, because clearing it means emptying the text, which commits `null`. The
 * capability survives; the visible shortcut to it does not.
 *
 * Neither difference is silent, which is the distinction that matters: a dropped `sx` or a dropped
 * ref is invisible at review time, whereas both of these are the first thing anyone opening the
 * refreshed field sees.
 */
export const toDsProps = ({
  error,
  format,
  fullWidth,
  hint,
  label,
  minDate,
  placeholder,
  variant,
}: ToDsPropsParams): DatePickerDsProps | null => {
  // The DS field is `w-full`; an MUI field without `fullWidth` has an intrinsic width of its own,
  // so mapping it would stretch the field and reflow its neighbours. The legacy wrapper defaults
  // this to `true`, so only a caller opting out lands here.
  if (fullWidth === false) {
    return null;
  }

  if (variant !== undefined && variant !== 'outlined') {
    return null;
  }

  // A caller who named a format asked for that format specifically. The refreshed field owns one
  // and cannot be told another, so honouring the request is impossible and ignoring it would be
  // the silent kind of wrong. Only the wrapper's own default falls through to the refreshed field.
  if (format !== undefined && format !== DEFAULT_DATE_FORMAT) {
    return null;
  }

  // Same reasoning for wording: the refreshed field's placeholder IS its format hint, so a caller
  // who wrote their own placeholder would simply not get it.
  if (placeholder !== undefined && placeholder !== LEGACY_DEFAULT_PLACEHOLDER) {
    return null;
  }

  /**
   * No label, no mapping - the same position `Inputs/TextField`'s mapper takes on a non-string
   * label, and for the same reason: the floating label is the field's only visible name.
   *
   * ── This used to map, via `hiddenLabel`, and that was wrong ───────────────────────────────────
   *
   * A field showing its label only until a value exists - which is what the wizard's occurrence
   * rows did - stopped passing `label` rather than passing an empty one. This mapper read that
   * absence as "the caller wants no visible name" and returned `hiddenLabel: true` with the
   * placeholder as the accessible name. It reproduced the legacy field's LOOK and broke two things
   * doing it:
   *
   *   The refreshed field is a FLOATING-label field. Filled is the state the label was drawn for -
   *   it shrinks to 12px and sits above the value (`datePickerLabelRecipe`). Hiding it left a
   *   filled field with dead space where its name belongs, which is what made the wizard's date
   *   row look unlabelled next to the time fields beside it.
   *
   *   The accessible name CHANGED on fill, from "Start Date" to "Select a date". A control whose
   *   name depends on whether it has a value is a worse defect than the visual one, and it was
   *   invisible because the test asserting it was written against the same mistaken intent.
   *
   * The legacy look is now asked for explicitly - `hideLabelWhenFilled` on the wrapper, honoured
   * only by the MUI branch - rather than inferred from a missing prop. See `DatePicker.tsx`.
   */
  if (label === undefined) {
    return null;
  }

  // An invalid bound would reach the refreshed field's `min` as `Invalid Date`, and comparisons
  // against it are all false - the bound silently stops bounding. MUI-X tolerates it today, so an
  // invalid `minDate` stays there, as `Inputs/TimePicker/toDsProps.ts` does for `minTime`.
  if (minDate !== undefined && minDate !== null && !isValid(minDate)) {
    return null;
  }

  return { label, helperText: hint, error, min: earliestSelectable(minDate) };
};
